import { useEffect, useState } from 'react';
import Hero from '../../../components/Hero';
import Layout from '../../../components/Layout';
import PortableText from '../../../components/PortableText';
import Section from '../../../components/Section';
import CampaignCard from '../../../components/cards/CampaignCard';
import LinkCard from '../../../components/cards/LinkCard';
import { newCarPageQuery } from '../../../groq/pages/newCarPage';
import { sanityClient } from '../../../lib/sanity.server';
import { MenuType, NewCarPageType, CampaignEntryType } from '../../../types';
import fetchSanityData from '../../../utils/fetchSanityData';
import { StandardArticleList } from '../../../components/StandardArticleList';
import { FeatureArticleList } from '../../../components/FeatureArticleList';

interface PageProps {
  data: {
    content: NewCarPageType;
    menus: MenuType;
  };
}

function NewCarPage({ data }: PageProps) {
  const {
    menus,
    content: {
      description,
      overline,
      title,
      featureArticles,
      standardArticles,
      image,
      linksProducts,
      linksServices,
      bodyText,
      seo,
      defaultSEO,
      campaignEntries,
    },
  } = data;

  const [isTransverseActive, setIsTransverseActive] = useState(false);
  const [useNewCarCampaignCard, setUseNewCarCampaignCard] = useState(false);
  const [campaignBrands, setCampaignBrands] = useState<string[] | undefined>(undefined);
  const [newCarCampaignsCard, setNewCarCampaignsCard] = useState<CampaignEntryType | undefined>(
    undefined,
  );

  useEffect(() => {
    const fetchCampaignBrands: () => void = async () => {
      const response = await fetchSanityData('newCarPageCampaigns');

      // Custom ordering of campaignBrands
      const ordering: { [key: string]: number } = {};
      const sortOrder = ['volkswagen', 'volkswagen-nyttekjoretoy', 'audi', 'skoda'];
      for (let i = 0; i < sortOrder.length; i++) ordering[sortOrder[i]] = i;
      response.campaignBrands?.sort((a: string, b: string) => ordering[a] - ordering[b]);
      setIsTransverseActive(response.isTransverseActive);
      setUseNewCarCampaignCard(response.useNewCarCampaignCard);
      setCampaignBrands(response.campaignBrands);
      setNewCarCampaignsCard(
        response.isTransverseActive && response.newCarCampaignsCardTransverse
          ? response.newCarCampaignsCardTransverse
          : response.newCarCampaignsCard,
      );
    };

    fetchCampaignBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const areActiveCampaigns = Array.isArray(campaignBrands) && campaignBrands?.length > 0;

  const useNewCarEntry = useNewCarCampaignCard && newCarCampaignsCard;

  return (
    <Layout menus={menus} seo={seo} defaultSEO={defaultSEO}>
      <Hero
        title={title}
        description={description}
        overline={overline}
        image={image}
        background="primary"
      />
      {/*   Does it make sense to display newCar-entry here? 
            Why not just list the brands with the campaigns? 
            Now the users are taken via the /kampanjer page 
      */}
      {useNewCarEntry && areActiveCampaigns && (
        <Section>
          <h2 className="mb-5 text-3xl font-semibold lg:mb-10 lg:text-4xl">Kampanjer</h2>
          <CampaignCard
            title={newCarCampaignsCard.title}
            description={newCarCampaignsCard.description}
            image={newCarCampaignsCard.image}
            overline={newCarCampaignsCard.overline}
            key={newCarCampaignsCard.title}
            href="/kampanjer"
          />
        </Section>
      )}
      {!useNewCarEntry && areActiveCampaigns && (
        <Section>
          <h2 className="mb-5 text-3xl font-semibold lg:mb-10 lg:text-4xl">Kampanjer</h2>
          <div className="grid gap-5">
            {Array.from(campaignBrands).map((brand) => (
              <CampaignCard
                title={campaignEntries[brand].title}
                description={campaignEntries[brand].description}
                image={campaignEntries[brand].image}
                overline={campaignEntries[brand].overline}
                key={campaignEntries[brand].title}
                href={
                  campaignEntries[brand].externalLandingPage ||
                  `/kjope-bil/nybil/${encodeURI(brand)}`
                }
              />
            ))}
          </div>
        </Section>
      )}
      <Section color="background">
        <h2 className="mb-5 text-3xl font-semibold lg:text-4xl">{linksProducts.title}</h2>
        <p className="mb-5 max-w-[60ch] text-xl lg:mb-10">{linksProducts.description}</p>

        <div className="grid gap-5 lg:grid-cols-2">
          {linksProducts?.links?.map((link) => {
            return (
              <LinkCard
                key={link.linkText}
                title={link.linkText}
                href={link.href}
                color="beige-light"
              />
            );
          })}
        </div>
      </Section>
      <Section color="info">
        <h2 className="mb-5 text-3xl font-semibold lg:text-4xl">{linksServices.title}</h2>
        <p className="mb-5 max-w-[60ch] text-xl lg:mb-10">{linksServices.description}</p>
        <div className="grid gap-5 lg:grid-cols-2">
          {linksServices.links?.map((service) => {
            return <LinkCard key={service.linkText} title={service.linkText} href={service.href} />;
          })}
        </div>
      </Section>
      {bodyText && (
        <div className="mx-auto my-10 max-w-3xl px-6 sm:px-10 lg:my-20">
          <PortableText value={bodyText} />
        </div>
      )}
      {standardArticles?.length > 0 && <StandardArticleList articles={standardArticles} />}
      {featureArticles?.length > 0 && <FeatureArticleList articles={featureArticles} />}
    </Layout>
  );
}

export async function getStaticProps() {
  const data = await sanityClient.fetch(newCarPageQuery, { currentDocumentSlug: 'nybil' });
  if (!data.content) return { notFound: true };

  return {
    props: { data },
    revalidate: 1,
  };
}

export default NewCarPage;
